import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core"
import { COLOR_GRAY } from "../../../constants"
import { ProjectsDashboardViewModel } from "../../api/projects-dashboard"
import { useTranslations } from "./useTranslations"
import ManagersBasedPieChart from "./ManagersBasedPieChart"
import CompletedProjectsScheduleGraph from "./CompletedProjectsScheduleGraph"
import CompletedProjectsBarGraph from "./CompletedProjectsBarGraph"
import { useState } from "react"
import { useRouter } from "../../../hooks"
import React from "react"

const useCompletedProjectsOverview = ({
  projectsDashboard,
  onBarGraphPeriodTypeChange,
}: CompletedProjectsOverviewProps) => {
  const { query: routerQuery } = useRouter()
  const [periodType, setPeriodType] = useState<"week" | "month" | "day">("month")
  const handleChange = (value: string) => {
    const selectedOption = value as "week" | "month" | "day"
    setPeriodType(selectedOption)
    onBarGraphPeriodTypeChange({ periodType: selectedOption })
  }

  React.useEffect(() => {
    if (routerQuery) {
      setPeriodType("month")
    }
  }, [routerQuery])

  return {
    projectsDashboard,
    periodType,
    handleChange,
  }
}

const CompletedProjectsOverview = (props: CompletedProjectsOverviewProps) => {
  const classes = useStyles()
  const { periodType, handleChange, projectsDashboard } = useCompletedProjectsOverview(props)
  const translations = useTranslations({
    completedProjectCount: projectsDashboard.totalCompletedProjects,
    projectCount: projectsDashboard.totalCompletedProjects,
    managerCount: projectsDashboard.totalNumberOfManagersInCompletedProjects,
  })

  const barGraphTitle = translations.completedBarCardHeader
  const completedProgressBarTitle = translations.scheduleLabel
  const managersPieChartTitle = translations.managersPieChartCardHeader

  const periodTypeOptions = [
    { value: "week", label: translations.weekLabel },
    { value: "month", label: translations.monthLabel },
    { value: "day", label: translations.dayLabel },
  ]

  return (
    <>
      <section className={classes.barGraphSection}>
        <Card variant="outlined" className={classes.card}>
          <CardHeader
            title={
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <Typography variant="body1">{barGraphTitle}</Typography>
                </Grid>
                <Grid item>
                  <TextField
                    fullWidth
                    select
                    label={translations.unitLabel}
                    value={periodType}
                    onChange={(e) => handleChange(e.target.value)}
                    variant="outlined"
                  >
                    {periodTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            }
          />
          <CardContent className={classes.pieChart}>
            <CompletedProjectsBarGraph projectsDashboard={projectsDashboard} />
          </CardContent>
        </Card>
      </section>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" className={classes.card}>
            <CardHeader title={<Typography variant="body1">{managersPieChartTitle}</Typography>} />
            <CardContent className={classes.pieChart}>
              <ManagersBasedPieChart
                totalNumberOfProjects={projectsDashboard.totalCompletedProjects}
                groupedProjects={projectsDashboard.groupedCompletedProjectsBasedOnManagers}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" className={classes.card}>
            <CardContent>
              <Box className={classes.barChartSection}>
                <Typography variant="body1">{completedProgressBarTitle}</Typography>
                <CompletedProjectsScheduleGraph projectsDashboard={projectsDashboard} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    height: "100%",
  },
  cardHeader: {
    color: COLOR_GRAY,
    fontSize: "1rem",
  },
  pieChart: {
    padding: theme.spacing(0, 2),
  },
  barChartSection: {
    padding: theme.spacing(0, 0, 4, 0),
  },
  barGraphSection: {
    padding: theme.spacing(0, 0, 1, 0),
  },
}))

export default CompletedProjectsOverview
type CompletedProjectsOverviewProps = {
  projectsDashboard: ProjectsDashboardViewModel
  onBarGraphPeriodTypeChange: (props: { periodType: "week" | "month" | "day" }) => void
}
